import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { useT } from '../i18n';
import DashboardLayout from '../components/DashboardLayout';
import AdminLoginModal from '../components/AdminLoginModal';
import { fetchTemplates, deleteTemplateApi } from '../api/templatesApi';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const ADMIN_EMAIL = 'davronking001@gmail.com';
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

async function adminFetch(path, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return res;
}

function formatDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(d);
  }
}

function formatBalance(b) {
  if (b === null || b === undefined) return '-';
  const n = Number(b);
  if (Number.isNaN(n)) return String(b);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseProjectName(jsonStr) {
  if (!jsonStr) return 'Untitled';
  try {
    const parsed = JSON.parse(jsonStr);
    return parsed?.project?.name || parsed?.projectName || 'Untitled';
  } catch {
    return 'Untitled';
  }
}

function projectStatus(p) {
  if (p.generated) return 'generated';
  if (p.expiredAt && new Date(p.expiredAt).getTime() < Date.now()) return 'expired';
  return 'draft';
}

export default function AdminPage() {
  const navigate = useNavigate();
  const t = useT();
  const accessToken = useStore((s) => s.accessToken);
  const userEmail = useStore((s) => s.userEmail);
  const loadUserMe = useStore((s) => s.loadUserMe);

  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('adminToken') || null);
  const [showLogin, setShowLogin] = useState(!adminToken);
  const [tab, setTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [usersPage, setUsersPage] = useState(0);
  const [projectsPage, setProjectsPage] = useState(0);
  const [usersSize, setUsersSize] = useState(DEFAULT_PAGE_SIZE);
  const [projectsSize, setProjectsSize] = useState(DEFAULT_PAGE_SIZE);
  const [usersTotalPages, setUsersTotalPages] = useState(0);
  const [projectsTotalPages, setProjectsTotalPages] = useState(0);
  const [usersTotalElements, setUsersTotalElements] = useState(0);
  const [projectsTotalElements, setProjectsTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if the logged-in user is not the admin email.
  useEffect(() => {
    if (!accessToken) { navigate('/'); return; }
    if (!userEmail) { loadUserMe(); return; }
    if (userEmail !== ADMIN_EMAIL) { navigate('/home'); }
  }, [accessToken, userEmail, loadUserMe, navigate]);

  const handleAuthFailure = useCallback(() => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setShowLogin(true);
  }, []);

  const readPageMeta = (payload) => {
    const meta = payload?.page || payload || {};
    return {
      totalPages: meta.totalPages ?? 0,
      totalElements: meta.totalElements ?? 0,
    };
  };

  const loadUsers = useCallback(async (page, size) => {
    if (!adminToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`/api/v1/admin/users?page=${page}&size=${size}`, adminToken);
      if (res.status === 401 || res.status === 403) { handleAuthFailure(); return; }
      const data = await res.json();
      if (data.success && data.data) {
        setUsers(data.data.content || []);
        const { totalPages, totalElements } = readPageMeta(data.data);
        setUsersTotalPages(totalPages);
        setUsersTotalElements(totalElements);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch {
      setError(t('error.connection'));
    } finally {
      setLoading(false);
    }
  }, [adminToken, handleAuthFailure, t]);

  const loadProjects = useCallback(async (page, size) => {
    if (!adminToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`/api/v1/admin/projects?page=${page}&size=${size}`, adminToken);
      if (res.status === 401 || res.status === 403) { handleAuthFailure(); return; }
      const data = await res.json();
      if (data.success && data.data) {
        setProjects(data.data.content || []);
        const { totalPages, totalElements } = readPageMeta(data.data);
        setProjectsTotalPages(totalPages);
        setProjectsTotalElements(totalElements);
      } else {
        setError(data.message || 'Failed to load projects');
      }
    } catch {
      setError(t('error.connection'));
    } finally {
      setLoading(false);
    }
  }, [adminToken, handleAuthFailure, t]);

  useEffect(() => {
    if (!adminToken) return;
    if (tab === 'users') loadUsers(usersPage, usersSize);
    else loadProjects(projectsPage, projectsSize);
  }, [tab, usersPage, usersSize, projectsPage, projectsSize, adminToken, loadUsers, loadProjects]);

  const handleLoginSuccess = (token) => {
    setAdminToken(token);
    setShowLogin(false);
  };

  if (userEmail && userEmail !== ADMIN_EMAIL) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="admin-page">
        <div className="admin-header">
          <h1 className="admin-title">{t('admin.panel')}</h1>
        </div>

        {adminToken && (
          <>
            <div className="admin-tabs">
              <button
                className={`admin-tab${tab === 'users' ? ' active' : ''}`}
                onClick={() => setTab('users')}
              >
                {t('admin.users')}
              </button>
              <button
                className={`admin-tab${tab === 'projects' ? ' active' : ''}`}
                onClick={() => setTab('projects')}
              >
                {t('admin.projects')}
              </button>
              <button
                className={`admin-tab${tab === 'templates' ? ' active' : ''}`}
                onClick={() => setTab('templates')}
              >
                {t('admin.templates')}
              </button>
            </div>

            {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

            {tab === 'users' && (
              <UsersTable
                users={users}
                loading={loading}
                page={usersPage}
                size={usersSize}
                totalPages={usersTotalPages}
                totalElements={usersTotalElements}
                onPage={setUsersPage}
                onSize={(s) => { setUsersSize(s); setUsersPage(0); }}
                t={t}
              />
            )}

            {tab === 'projects' && (
              <ProjectsTable
                projects={projects}
                loading={loading}
                page={projectsPage}
                size={projectsSize}
                totalPages={projectsTotalPages}
                totalElements={projectsTotalElements}
                onPage={setProjectsPage}
                onSize={(s) => { setProjectsSize(s); setProjectsPage(0); }}
                navigate={navigate}
                t={t}
              />
            )}

            {tab === 'templates' && (
              <TemplatesTable navigate={navigate} t={t} />
            )}
          </>
        )}
      </div>

      {showLogin && (
        <AdminLoginModal
          onSuccess={handleLoginSuccess}
          onCancel={() => {
            setShowLogin(false);
            if (!adminToken) navigate('/home');
          }}
        />
      )}
    </DashboardLayout>
  );
}

function UsersTable({ users, loading, page, size, totalPages, totalElements, onPage, onSize, t }) {
  return (
    <div className="admin-table-wrap">
      {loading ? (
        <div className="admin-loading">{t('home.loading')}</div>
      ) : users.length === 0 ? (
        <div className="admin-empty">{t('admin.noUsers')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.col.name')}</th>
              <th>{t('admin.col.email')}</th>
              <th>{t('admin.col.provider')}</th>
              <th>{t('admin.col.verified')}</th>
              <th>{t('admin.col.status')}</th>
              <th>{t('admin.col.balance')}</th>
              <th>{t('admin.col.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name || '-'}</td>
                <td>{u.email}</td>
                <td>{u.authProvider || 'local'}</td>
                <td>
                  <span className={`admin-badge${u.verified ? ' ok' : ' warn'}`}>
                    {u.verified ? t('profilePage.verified') : t('profilePage.notVerified')}
                  </span>
                </td>
                <td>
                  <span className={`admin-badge${u.status === 'ACTIVE' ? ' ok' : ' warn'}`}>
                    {u.status || '-'}
                  </span>
                </td>
                <td className="admin-num">{formatBalance(u.balance)}</td>
                <td>{formatDate(u.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pager
        page={page}
        size={size}
        totalPages={totalPages}
        totalElements={totalElements}
        onPage={onPage}
        onSize={onSize}
        t={t}
      />
    </div>
  );
}

function ProjectsTable({ projects, loading, page, size, totalPages, totalElements, onPage, onSize, navigate, t }) {
  const openView = (p) => {
    const projectName = parseProjectName(p.projectJson);
    navigate('/admin/view', {
      state: {
        projectJson: p.projectJson,
        projectName,
        projectId: p.id,
      },
    });
  };

  return (
    <div className="admin-table-wrap">
      {loading ? (
        <div className="admin-loading">{t('home.loading')}</div>
      ) : projects.length === 0 ? (
        <div className="admin-empty">{t('admin.noProjects')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.col.projectName')}</th>
              <th>{t('admin.col.owner')}</th>
              <th>{t('admin.col.ownerEmail')}</th>
              <th>{t('admin.col.status')}</th>
              <th>{t('admin.col.createdAt')}</th>
              <th>{t('admin.col.expiredAt')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => {
              const status = projectStatus(p);
              return (
                <tr key={p.id}>
                  <td>{parseProjectName(p.projectJson)}</td>
                  <td>{p.userName || '-'}</td>
                  <td>{p.userEmail || '-'}</td>
                  <td>
                    <span className={`admin-badge${status === 'generated' ? ' ok' : status === 'expired' ? ' warn' : ''}`}>
                      {t(`admin.status.${status}`)}
                    </span>
                  </td>
                  <td>{formatDate(p.createdAt)}</td>
                  <td>{formatDate(p.expiredAt)}</td>
                  <td>
                    <button
                      className="admin-view-btn"
                      onClick={() => openView(p)}
                      disabled={!p.projectJson}
                    >
                      {t('admin.view')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <Pager
        page={page}
        size={size}
        totalPages={totalPages}
        totalElements={totalElements}
        onPage={onPage}
        onSize={onSize}
        t={t}
      />
    </div>
  );
}

function TemplatesTable({ navigate, t }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    fetchTemplates()
      .then(setTemplates)
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolveName = (tpl) => tpl.name || tpl.id;
  const resolveDesc = (tpl) => tpl.description || '';

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await deleteTemplateApi(id);
      setConfirmDelete(null);
      load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-table-wrap">
      <div className="admin-templates-header">
        <span className="admin-page-info">
          {loading ? '...' : `${templates.length} ${t('admin.totalRows')}`}
        </span>
        <button
          className="admin-view-btn"
          onClick={() => navigate('/admin/template/new')}
        >
          + {t('admin.template.new')}
        </button>
      </div>

      {error && <div className="auth-error" style={{ margin: 12 }}>{error}</div>}

      {loading ? (
        <div className="admin-loading">{t('home.loading')}</div>
      ) : templates.length === 0 ? (
        <div className="admin-empty">{t('admin.template.empty')}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.col.name')}</th>
              <th>{t('admin.template.descShort')}</th>
              <th>{t('admin.template.source')}</th>
              <th>{t('admin.template.entities')}</th>
              <th>{t('admin.col.createdAt')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl) => (
              <tr key={tpl.id}>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 10, height: 10, borderRadius: 3, background: tpl.color || '#999',
                    }} />
                    {resolveName(tpl)}
                  </span>
                </td>
                <td style={{ maxWidth: 320, color: 'var(--text-secondary)', fontSize: 12 }}>
                  {resolveDesc(tpl)}
                </td>
                <td>
                  <span className={`admin-badge${tpl.builtin ? ' ok' : ' warn'}`}>
                    {tpl.builtin ? t('admin.template.builtin') : t('admin.template.custom')}
                  </span>
                </td>
                <td className="admin-num">{tpl.entityCount}</td>
                <td>{formatDate(tpl.createdAt)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button
                    className="admin-view-btn"
                    style={{ marginRight: 6 }}
                    onClick={() => navigate(`/admin/template/edit/${tpl.id}`)}
                  >
                    {t('admin.template.edit')}
                  </button>
                  <button
                    className="admin-page-btn"
                    style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                    onClick={() => setConfirmDelete(tpl)}
                  >
                    {t('delete.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDelete && (
        <div className="confirm-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">{t('admin.template.deleteTitle')}</h3>
            <p className="confirm-desc">
              {t('admin.template.deleteConfirm')} <strong>"{resolveName(confirmDelete)}"</strong>?
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setConfirmDelete(null)} disabled={deleting}>
                {t('profile.cancel')}
              </button>
              <button className="confirm-delete" onClick={() => handleDelete(confirmDelete.id)} disabled={deleting}>
                {deleting ? t('delete.deleting') : t('delete.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pager({ page, size, totalPages, totalElements, onPage, onSize, t }) {
  const hasMultiplePages = totalPages > 1;
  return (
    <div className="admin-pager">
      <div className="admin-pager-left">
        <label className="admin-pager-size">
          <span>{t('admin.rowsPerPage')}</span>
          <select
            value={size}
            onChange={(e) => onSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {totalElements > 0 && (
          <span className="admin-page-info">
            {totalElements} {t('admin.totalRows')}
          </span>
        )}
      </div>
      {hasMultiplePages && (
        <div className="admin-pager-right">
          <button
            className="admin-page-btn"
            onClick={() => onPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            {t('feedback.prev')}
          </button>
          <span className="admin-page-info">{page + 1} / {totalPages}</span>
          <button
            className="admin-page-btn"
            onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            {t('feedback.next')}
          </button>
        </div>
      )}
    </div>
  );
}
